import { localStorageNameEnum } from "../models/constants";

export const statusMessages = {
  200: "OK",
  201: "Created",
  202: "Accepted",
  203: "Non-Authoritative Information",
  204: "No Content",
  205: "Reset Content",
  206: "Partial Content",
  207: "Multi-Status (WebDAV)",
  208: "Already Reported (WebDAV)",
  226: "IM Used",
  300: "Multiple Choices",
  301: "Moved Permanently",
  302: "Found",
  303: "See Other",
  304: "Not Modified",
  305: "Use Proxy",
  306: "(Unused)",
  307: "Temporary Redirect",
  308: "Permanent Redirect (experimental)",
  // User-facing fallback strings for the most common error codes.
  // These only render when the backend doesn't supply a `message` field on the
  // response body — when it does (e.g., {"message":"bad credentials"}) we show
  // that verbatim. Worded so the user knows what to do next, not just what
  // happened.
  400: "We couldn't process that request. Double-check the fields and try again.",
  401: "Sign in failed. Check your email and password, then try again.",
  402: "Payment Required",
  403: "You don't have access to that.",
  404: "We couldn't find what you were looking for.",
  405: "Method Not Allowed",
  406: "Not Acceptable",
  407: "Proxy Authentication Required",
  408: "Request Timeout",
  409: "Conflict",
  410: "Gone",
  411: "Length Required",
  412: "Precondition Failed",
  413: "Request Entity Too Large",
  414: "Request-URI Too Long",
  415: "Unsupported Media Type",
  416: "Requested Range Not Satisfiable",
  417: "Expectation Failed",
  418: "I'm a teapot (RFC 2324)",
  420: "Enhance Your Calm (Twitter)",
  422: "Unprocessable Entity (WebDAV)",
  423: "Locked (WebDAV)",
  424: "Failed Dependency (WebDAV)",
  425: "Reserved for WebDAV",
  426: "Upgrade Required",
  428: "Precondition Required",
  429: "Too Many Requests",
  431: "Request Header Fields Too Large",
  444: "No Response (Nginx)",
  449: "Retry With (Microsoft)",
  450: "Blocked by Windows Parental Controls (Microsoft)",
  451: "Unavailable For Legal Reasons",
  499: "Client Closed Request (Nginx)",
  500: "Something went wrong on our side. Please try again in a moment.",
  501: "Not Implemented",
  502: "We can't reach the server right now. Please try again in a moment.",
  503: "The service is temporarily unavailable. Please try again shortly.",
  504: "The server took too long to respond. Please try again.",
  505: "HTTP Version Not Supported",
  506: "Variant Also Negotiates (Experimental)",
  507: "Insufficient Storage (WebDAV)",
  508: "Loop Detected (WebDAV)",
  509: "Bandwidth Limit Exceeded (Apache)",
  510: "Not Extended",
  511: "Network Authentication Required",
  598: "Network read timeout error",
  599: "Network connect timeout error",
};

/**
 * Returns only two variants of the result:
 *  1) In case of successful result:  { value: <returned value> }
 *  2) When something goes wrong: { error: <error message> }
 * @param {import("types/commonTypes").HttpServiceProps} props
 * @template T
 * @returns {Promise<import("types/commonTypes").HttpResult<T>>}
 */
export const httpService = async ({
  url,
  options,
  token,
  forbiddenCallback,
  mattermost = false,
}) => {
  const storedToken = window.sessionStorage.getItem(
    localStorageNameEnum.AUTH_TOKEN
  );
  const { data } = options;

  const headers = {
    "Content-Type": "application/json",
  };

  if (token !== undefined) {
    headers.Authorization = `Bearer ${token}`;
  } else if (storedToken && url !== "/auth/refresh") {
    headers.Authorization = `Bearer ${storedToken}`;
  }

  const apiURL = mattermost
    ? process.env.REACT_APP_MATTERMOST_API_URL
    : process.env.REACT_APP_API_URL;

  try {
    const response = await fetch(`${apiURL}${url}`, {
      ...options,
      headers,
      body: JSON.stringify(data),
    });

    if (response?.status === 401 || response?.status === 403) {
      if (typeof forbiddenCallback === "function") {
        forbiddenCallback();
      } else {
        throw new Error(statusMessages[response?.status]);
      }
    }

    const contentType = response?.headers?.get("Content-Type") || "";
    let json;

    if (contentType.includes("application/json")) {
      try {
        json = await response.json();
      } catch (e) {
        throw new Error(`JSON Error in API response: ${e.message}`);
      }
    }

    // Capture the requestId from either the response body (backend ApiError
    // sets it from MDC.get("requestId") — see backend ApiError.java) or the
    // X-Request-Id response header (RequestIdFilter, backend PR #143). Either
    // path gives support a single string they can plug into CloudWatch /
    // Grafana to find the exact request in logs.
    const requestId =
      json?.requestId || response?.headers?.get("X-Request-Id") || null;

    if (!response?.ok) {
      const baseMessage = response?.status
        ? json?.message && json.message !== ""
          ? json.message
          : statusMessages[response?.status]
        : "Network Error";
      // Suffix the message with the requestId so legacy callers that only
      // read `error` still get it surfaced to the user. New code should
      // prefer the structured `requestId` field on the return value.
      const messageWithId = requestId
        ? `${baseMessage} [Request ID: ${requestId}]`
        : baseMessage;
      const err = new Error(messageWithId);
      err.requestId = requestId;
      throw err;
    }

    return {
      value: json,
      requestId,
    };
  } catch (error) {
    console.error(`httpService`, error);
    return {
      error: String(error.message).replace(/Error:/g, ""),
      requestId: error.requestId || null,
    };
  }
};

/**
 * Converts an object into a query string for a GET request.
 *
 * @param {Record<string, any>} params - The object to be converted.
 * @returns {string} The query string.
 */
export const convertToQueryString = (params) => {
  return (
    "?" +
    Object.keys(params)
      .map(
        (key) => encodeURIComponent(key) + "=" + encodeURIComponent(params[key])
      )
      .join("&")
  );
};
